/**
 * Defaults + option lists for workspace Org Design Insights hierarchy knobs.
 * Keep in sync with teamform-api computeSupplyInsightStats defaults and
 * teamform-ai-config docs/metrics/org-design-insights.md
 */

export const LAYER_ROOT_MODE_OPTIONS = [
  {
    value: "supply_root",
    label: "Top of the organisation",
    description:
      "Count layers from the team set as the top of your organisation — not any grouping above it",
  },
  {
    value: "viewed_team",
    label: "The team being viewed",
    description: "Count layers relative to whichever team is open in Insights",
  },
  {
    value: "offset_from_supply_root",
    label: "A level below the top",
    description:
      "Start counting at a chosen level below the top team — for example Division",
  },
] as const;

export const LAYER_INDEX_ORIGIN_OPTIONS = [
  {
    value: "apex_zero",
    label: "Top leader = layer 0",
    description:
      "Zero-based numbering, common in data and engineering tools — the top leader is layer 0",
  },
  {
    value: "apex_one",
    label: "Top leader = layer 1",
    description:
      "One-based numbering, used by most published benchmarks (for example McKinsey and Deloitte layer studies) — the top leader is layer 1",
  },
] as const;

export const DEPTH_MEASURE_OPTIONS = [
  {
    value: "hops_from_apex",
    label: "Steps below the top",
    description:
      "Counts management steps below the starting point — a leader with one level of reports measures 1",
  },
  {
    value: "levels_including_apex",
    label: "Levels including the top",
    description:
      "Counts every level including the top leader — the same organisation measures 2",
  },
] as const;

export const OWNER_MEMBER_MODE_OPTIONS = [
  {
    value: "unique_people",
    label: "Count each person once (recommended)",
    description:
      "An owner who is also listed as a member counts once, and never inside their own span",
  },
  {
    value: "exclude_owner_from_member_headcount",
    label: "Leave the owner out of team size",
    description:
      "Team size omits the primary owner when they are also listed as a member",
  },
] as const;

export const ORG_CHART_COMPARE_DEFAULT_OPTIONS = [
  {
    value: "off",
    label: "Off",
    description: "Compare stays empty until someone picks dates",
  },
  {
    value: "last_30",
    label: "Last 30 days",
    description: "From 30 days ago through today (end date always updates)",
  },
  {
    value: "last_60",
    label: "Last 60 days",
    description: "From 60 days ago through today (end date always updates)",
  },
  {
    value: "last_90",
    label: "Last 90 days",
    description: "From 90 days ago through today (end date always updates)",
  },
  {
    value: "from_date",
    label: "From a specific date",
    description: "Chosen start date through today (end date always updates)",
  },
] as const;

export type OrgChartCompareDefaultMode =
  (typeof ORG_CHART_COMPARE_DEFAULT_OPTIONS)[number]["value"];

export type InsightsHierarchyConfig = {
  layerRootMode: (typeof LAYER_ROOT_MODE_OPTIONS)[number]["value"];
  layerRootOffset: number;
  layerIndexOrigin: (typeof LAYER_INDEX_ORIGIN_OPTIONS)[number]["value"];
  depthMeasure: (typeof DEPTH_MEASURE_OPTIONS)[number]["value"];
  ownerMemberMode: (typeof OWNER_MEMBER_MODE_OPTIONS)[number]["value"];
  syncTeamNameFromPrimaryOwner: boolean;
  showTargets: boolean;
  orgChartCompareDefault: OrgChartCompareDefaultMode;
  orgChartCompareFromDate: string | null;
  orgChartCompareOnByDefault: boolean;
};

/** Narrow literals via `as const`; `satisfies` keeps assignable to the full config union. */
export const DEFAULT_INSIGHTS_HIERARCHY = {
  layerRootMode: "supply_root",
  layerRootOffset: 0,
  layerIndexOrigin: "apex_zero",
  depthMeasure: "hops_from_apex",
  ownerMemberMode: "unique_people",
  syncTeamNameFromPrimaryOwner: false,
  showTargets: false,
  orgChartCompareDefault: "off",
  orgChartCompareFromDate: null,
  orgChartCompareOnByDefault: false,
} as const satisfies InsightsHierarchyConfig;

const ORG_CHART_COMPARE_DEFAULT_VALUES = new Set<string>(
  ORG_CHART_COMPARE_DEFAULT_OPTIONS.map((o) => o.value)
);

const API_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** showTargets defaults off. Honour legacy showInsightsTargets if present. */
const resolveShowTargets = (raw: unknown): boolean => {
  const record =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
  if (!record) {
    return false;
  }
  if ("showTargets" in record) {
    return Boolean(record["showTargets"]);
  }
  if ("showInsightsTargets" in record) {
    return Boolean(record["showInsightsTargets"]);
  }
  return false;
};

const resolveOrgChartCompareDefault = (
  raw: unknown
): {
  orgChartCompareDefault: OrgChartCompareDefaultMode;
  orgChartCompareFromDate: string | null;
  orgChartCompareOnByDefault: boolean;
} => {
  const record =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const modeRaw = record["orgChartCompareDefault"];
  const legacyOn = Boolean(record["orgChartCompareOnByDefault"]);
  const orgChartCompareDefault = (
    typeof modeRaw === "string" && ORG_CHART_COMPARE_DEFAULT_VALUES.has(modeRaw)
      ? modeRaw
      : legacyOn
        ? "last_90"
        : "off"
  ) as OrgChartCompareDefaultMode;

  const fromRaw = record["orgChartCompareFromDate"];
  const orgChartCompareFromDate =
    typeof fromRaw === "string" && API_DATE_RE.test(fromRaw) ? fromRaw : null;

  return {
    orgChartCompareDefault,
    orgChartCompareFromDate:
      orgChartCompareDefault === "from_date" ? orgChartCompareFromDate : null,
    orgChartCompareOnByDefault: orgChartCompareDefault !== "off",
  };
};

export const resolveInsightsHierarchy = (
  raw: unknown
): InsightsHierarchyConfig => {
  const record =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const {
    targets: _targets,
    odInsightsDataSource: _soak,
    allowLensSwitching: _lens,
    ...rest
  } = record;
  const compare = resolveOrgChartCompareDefault(raw);
  return {
    ...DEFAULT_INSIGHTS_HIERARCHY,
    ...rest,
    layerRootOffset: Math.max(
      0,
      Math.floor(Number(record["layerRootOffset"]) || 0)
    ),
    syncTeamNameFromPrimaryOwner: Boolean(
      record["syncTeamNameFromPrimaryOwner"]
    ),
    showTargets: resolveShowTargets(raw),
    ...compare,
  };
};

export const supplyTeamDepth = (
  hierarchyIds?: string[] | null,
  apexTeamId?: string | null
): number => {
  if (!hierarchyIds?.length) {
    return 0;
  }
  if (!apexTeamId) {
    return Math.max(0, hierarchyIds.length - 1);
  }
  const apexIdx = hierarchyIds.indexOf(apexTeamId);
  if (apexIdx < 0) {
    return Math.max(0, hierarchyIds.length - 1);
  }
  return Math.max(0, hierarchyIds.length - 1 - apexIdx);
};

export const nearestApexOnPath = (
  hierarchyIds: string[] | null | undefined,
  apexTeamIds: readonly string[]
): string | null => {
  if (!hierarchyIds?.length || apexTeamIds.length === 0) {
    return null;
  }
  const apexSet = new Set(apexTeamIds);
  for (let i = hierarchyIds.length - 1; i >= 0; i -= 1) {
    const id = hierarchyIds[i];
    if (id && apexSet.has(id)) {
      return id;
    }
  }
  return null;
};

export type LayerRootSelection = {
  layerRootMode: "supply_root" | "offset_from_supply_root";
  layerRootOffset: number;
};

export const layerRootConfigFromSupplyTeam = (
  team: {
    id?: string;
    hierarchyIds?: string[] | null;
  },
  apexTeamId?: string | null
): LayerRootSelection => {
  if (apexTeamId && team.id === apexTeamId) {
    return { layerRootMode: "supply_root", layerRootOffset: 0 };
  }
  const offset = supplyTeamDepth(team.hierarchyIds, apexTeamId ?? null);
  if (offset === 0) {
    return { layerRootMode: "supply_root", layerRootOffset: 0 };
  }
  return {
    layerRootMode: "offset_from_supply_root",
    layerRootOffset: offset,
  };
};

export const teamsAtSupplyDepth = <
  T extends { hierarchyIds?: string[] | null },
>(
  teams: T[],
  depth: number,
  apexTeamId?: string | null
): T[] =>
  teams.filter(
    (team) => supplyTeamDepth(team.hierarchyIds, apexTeamId) === depth
  );

export const effectiveLayerRootDepth = (config: {
  layerRootMode?: string;
  layerRootOffset?: number;
}): number | null => {
  if (config.layerRootMode === "viewed_team") {
    return null;
  }
  if (config.layerRootMode === "offset_from_supply_root") {
    return Math.max(0, Math.floor(Number(config.layerRootOffset) || 0));
  }
  return 0;
};
